import React from 'react';
import { 
  Briefcase, MapPin, Clock, ChevronDown, ChevronUp,
  Download, ArrowRight, Building2, Users, Heart
} from 'lucide-react';
import { Job } from '../../types/careers';

interface JobCardProps {
  job: Job;
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (job: Job) => void;
  onApply: (job: Job) => void;
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  isExpanded,
  onToggle,
  onDownload,
  onApply
}) => {
  const getTypeIcon = () => {
    switch (job.type) {
      case 'full-time':
        return <Building2 size={16} />;
      case 'remote':
        return <Users size={16} />;
      case 'part-time':
        return <Heart size={16} />;
      default:
        return <Briefcase size={16} />;
    }
  };

  const getTypeLabel = () => {
    switch (job.type) {
      case 'full-time':
        return 'Temps plein';
      case 'remote':
        return 'À distance';
      case 'part-time':
        return 'Temps partiel';
      default:
        return job.type;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:border-primary-200 transition-all hover:shadow-xl">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
          <button
            onClick={onToggle}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? "Réduire" : "Développer"}
          >
            {isExpanded ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
        </div>
        
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
          <span className="flex items-center">
            <Briefcase size={16} className="mr-2" />
            {job.department}
          </span>
          <span className="flex items-center">
            <MapPin size={16} className="mr-2" />
            {job.location}
          </span>
          <span className="flex items-center">
            {getTypeIcon()}
            <span className="ml-2">{getTypeLabel()}</span>
          </span>
          <span className="flex items-center">
            <Clock size={16} className="mr-2" />
            {job.experience}
          </span>
        </div>

        <p className="text-gray-600 mb-4">{job.description}</p>

        {isExpanded && (
          <div className="mt-6 space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Prérequis</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                {job.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Responsabilités</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                {job.responsibilities.map((resp, index) => (
                  <li key={index}>{resp}</li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Avantages</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                {job.benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                onClick={() => onDownload(job)}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center"
              >
                <Download size={20} className="mr-2" />
                Télécharger PDF
              </button>
              <button
                onClick={() => onApply(job)}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center"
              >
                Postuler
                <ArrowRight size={20} className="ml-2" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;